import { useEffect } from "react";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://blinkmed.com.br";

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = "website",
  jsonLd,
  noindex = false,
}: SeoHeadProps) {
  useEffect(() => {
    const brandName = "Blink Telemedicina";
    const fullTitle = title.startsWith(brandName) || title.endsWith(brandName) ? title : `${title} | ${brandName}`;
    document.title = fullTitle;

    const upsertMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const upsertLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    upsertMeta("description", description);
    if (keywords) upsertMeta("keywords", keywords);
    upsertMeta("og:title", fullTitle, true);
    upsertMeta("og:description", description, true);
    upsertMeta("og:type", ogType, true);
    if (ogImage) upsertMeta("og:image", ogImage, true);
    upsertMeta("twitter:title", fullTitle);
    upsertMeta("twitter:description", description);
    if (ogImage) upsertMeta("twitter:image", ogImage);

    const canonicalUrl = canonicalPath
      ? `${SITE_URL}${canonicalPath}`
      : SITE_URL;
    upsertLink("canonical", canonicalUrl);
    upsertMeta("og:url", canonicalUrl, true);

    upsertMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

    const existingScript = document.getElementById("seo-jsonld");
    if (existingScript) existingScript.remove();

    if (jsonLd && !noindex) {
      const script = document.createElement("script");
      script.id = "seo-jsonld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById("seo-jsonld");
      if (script) script.remove();
    };
  }, [title, description, keywords, canonicalPath, ogImage, ogType, jsonLd, noindex]);

  return null;
}